/**
 * CounterNode - generates a sequential pattern from min to max
 *
 * Pure pattern generator. Outputs a pattern array [min, min+step, ..., max].
 * Connect output to PatternPlayerNode for traversal.
 *
 * Endpoints:
 * - min, max, step (input): range parameters
 * - pattern (output): generated pattern array
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { ParamKeys, params } from '@/nodes/TypedEndpoints';

const endpoints = params({
  min: { value: 1, min: 1, max: 100 },
  max: { value: 10, min: 1, max: 100 },
  step: { value: 1, min: 0.1, max: 10, step: 0.1 },
  pattern: { dataType: 'array', isOutput: true },
});

export class CounterNode extends BaseNode<typeof endpoints> {
  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.generatePattern();
  }

  onParamChanged(payload: Payload, endpointId: ParamKeys<typeof endpoints>): void {
    if (endpointId === 'min' || endpointId === 'max' || endpointId === 'step') {
      this.generatePattern();
    }
  }

  private generatePattern(): void {
    const min = this.params.min.value;
    const max = this.params.max.value;
    const step = Math.abs(this.params.step.value);

    const values: number[] = [];

    if (min <= max) {
      for (let i = min; i <= max; i += step) {
        values.push(i);
      }
    } else {
      // Count down if min > max
      for (let i = min; i >= max; i -= step) {
        values.push(i);
      }
    }

    this.params.pattern.set(values);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const counterNodeDef: NodeDef<CounterNode> = {
  type: 'Counter',
  shortName: 'counter',
  info: 'Generates a sequential pattern array [min, min+step, ..., max]. Connect to PatternPlayer for traversal.',
  domain: Domain.Data,
  endpoints,
  ctor: CounterNode
};
